import type { Node } from '@xyflow/react';
import {
  ICalculationFields,
  ICalculationInfo,
  ICalculationStep,
  ICondition,
  IConditionFilters,
  ICurrentCalculationSession,
} from '../../redux/Calculations/CurrentCalculationSessionInterface';
import { IParameterSessionProps } from '../../redux/Parameters/ParameterInterface';
import { messages } from '../CommonFunctions/aliases';
import {
  equalPatternCollections,
  withPatternCollections,
} from '../FilterUtils/FilterPatternCollection';
import { IFlow } from './Conditions/Evaluation/EvaluationInterface';
import { functionDefinitions, lengthOfSourceArray, SourceFunction } from './constants';
import { format } from 'date-fns';
export const getFlowTypeFromFunctionName = (functionName: string) => {
  const allFunctions = Object.keys(functionDefinitions).filter((key) => key !== 'All');
  const functionType = allFunctions.find((type) =>
    functionDefinitions[type].some(
      (func) => (func.flowName ? func.flowName : func.fieldName) === functionName,
    ),
  );

  return functionType || null;
};

export const getFlowFieldNameIfExists = (functionName: string) => {
  // only for string and number functions, not sure about date, condition, aggregation

  return (
    functionDefinitions.All.find(
      (func) => (func.flowName ? func.flowName : func.fieldName) === functionName,
    )?.fieldName || null
  );
};

export const getCalculationFlow = (
  startFromFlowId: string,
  startFromType: string,
  calculationSession: ICurrentCalculationSession,
): IFlow => {
  const deps: { id: string; type: string }[] = [
    {
      id: startFromFlowId,
      type: startFromType,
    },
  ];
  const calculatedFieldName = calculationSession.flowDisplayNames[startFromFlowId];
  const fields: ICalculationFields = {};
  const flows: {
    [key: string]: ICalculationStep[];
  } = {};
  const conditionFilters: IConditionFilters = {};
  let idx = 0;
  while (idx < deps.length) {
    const dep = deps[idx];
    if (dep.type === 'flow') {
      const flow = calculationSession.calculationInfo!.flows[dep.id];
      if (flow) {
        const alreadyFoundDeps = flows[dep.id];
        if (!alreadyFoundDeps) {
          flows[dep.id] = flow;
        }
      }
      const flowName = calculationSession.flowDisplayNames[dep.id];
      if (flowName === 'If Else') {
        const filterIds = flow.flatMap((step) => step.filter);
        const _conditionFilters = calculationSession.calculationInfo!.conditionFilters;

        filterIds.forEach((filterId, idx) => {
          if (!filterId || !_conditionFilters[filterId]) return;
          const alreadyFoundDeps = conditionFilters[filterId];
          if (alreadyFoundDeps) return;
          conditionFilters[filterId] = _conditionFilters[filterId];
          const conditions = _conditionFilters[filterId][0].conditions;
          conditions.forEach((condition) => {
            const leftOperand = condition.leftOperand;
            const rightOperand = condition.rightOperand;
            const leftOperandType = condition.leftOperandType;
            const rightOperandType = condition.rightOperandType;
            if (leftOperandType[0] === 'field') {
              const field = calculationSession.calculationInfo!.fields[leftOperand[0] as string];
              if (field) {
                fields[leftOperand[0] as string] = field;
                deps.push({
                  id: leftOperand[0] as string,
                  type: 'field',
                });
              }
            } else if (leftOperandType[0] === 'flow') {
              const _flow = calculationSession.calculationInfo!.flows[leftOperand[0] as string];
              if (_flow) {
                const alreadyFoundDeps = flows[leftOperand[0] as string];
                if (!alreadyFoundDeps) {
                  flows[leftOperand[0] as string] = _flow;
                  deps.push({
                    id: leftOperand[0] as string,
                    type: 'flow',
                  });
                }
              }
            }

            rightOperandType?.forEach((type, index) => {
              if (type === 'field') {
                const field =
                  calculationSession.calculationInfo!.fields[rightOperand![index] as string];
                if (field) {
                  fields[rightOperand![index] as string] = field;
                  deps.push({
                    id: rightOperand![index] as string,
                    type: 'field',
                  });
                }
              } else if (type === 'flow') {
                const _flow =
                  calculationSession.calculationInfo!.flows[rightOperand![index] as string];
                if (_flow) {
                  const alreadyFoundDeps = flows[rightOperand![index] as string];
                  if (!alreadyFoundDeps) {
                    flows[rightOperand![index] as string] = _flow;
                    deps.push({
                      id: rightOperand![index] as string,
                      type: 'flow',
                    });
                  }
                }
              }
            });
          });
        });
      }
      const sourceTypes = flow.flatMap((step) => step.sourceType);
      const sources = flow.flatMap((step) => step.source);
      sourceTypes.forEach((sourceType, index) => {
        if (sourceType === 'field') {
          const field = calculationSession.calculationInfo!.fields[sources[index]];
          if (field) {
            const alreadyFoundDeps = fields[sources[index]];
            if (!alreadyFoundDeps) {
              fields[sources[index]] = field;
              deps.push({
                id: sources[index],
                type: 'field',
              });
            }
          }
        } else if (sourceType === 'flow') {
          const _flow = calculationSession.calculationInfo!.flows[sources[index]];
          if (_flow) {
            const alreadyFoundDeps = flows[sources[index]];
            if (!alreadyFoundDeps) {
              flows[sources[index]] = _flow;
              deps.push({
                id: sources[index],
                type: 'flow',
              });
            }
          }
        }
      });
    } else {
      const field = calculationSession.calculationInfo!.fields[dep.id];
      if (field) {
        const alreadyFoundDeps = fields[dep.id];
        if (!alreadyFoundDeps) fields[dep.id] = field;
      }
    }
    idx++;
  }
  return {
    calculatedFieldName: calculatedFieldName,
    flows: reOrderKeysOfObject(flows, 'reverse'),
    fields: fields,
    conditionFilters: conditionFilters,
  };
};

export const reOrderKeysOfObject = <T extends Record<string, any>>(
  obj: T,
  order: 'reverse' | 'asc' | 'desc' | string[] = 'reverse',
): T => {
  let keys = Object.keys(obj);

  if (Array.isArray(order)) {
    keys = order.filter((key) => key in obj);
  } else if (order === 'asc') {
    keys.sort();
  } else if (order === 'desc') {
    keys.sort().reverse();
  } else if (order === 'reverse') {
    keys.reverse();
  }

  return keys.reduce(
    (acc, key) => {
      (acc as Record<string, any>)[key] = obj[key];
      return acc;
    },
    {} as Record<string, any>,
  ) as T;
};

export const orderByDependency = (calculationInfo: ICalculationInfo) => {
  if (!calculationInfo)
    return {
      error: true,
      message: 'Calculation details not found',
      orderedFlows: [],
    };
  const genDependencyTracker: { [key: string]: boolean } = {};
  const genSortedDependencyStack: string[] = [];
  const flowSorter = (flow: string, visited: Set<string>, calculationInfo: ICalculationInfo) => {
    if (genSortedDependencyStack.includes(flow)) return;
    if (visited.has(flow)) {
      //   throw new Error(`Cycle detected involving flow: ${flow}`);
      throw new Error(messages.formula.cyclicDependency);
    }

    visited.add(flow);
    genDependencyTracker[flow] = true;

    for (const step of calculationInfo.flows[flow]) {
      const sourceList = step.source.filter(
        (source: string, index: number) => step.sourceType[index] === 'flow',
      );

      if (step.flow === 'IfElse') {
        const filterId = step.filter;
        if (!filterId) throw new Error('Condition not found in IfElse condition');
        const filter = calculationInfo.conditionFilters[filterId][0];

        for (const condition of filter.conditions) {
          const leftOperandType = condition.leftOperandType[0];
          if (leftOperandType === 'flow' && !sourceList.includes(condition.leftOperand[0])) {
            sourceList.push(condition.leftOperand[0]);
          }

          const rightOperandType = condition.rightOperandType?.[0];
          if (rightOperandType === 'flow' && condition.rightOperand?.[0]) {
            sourceList.push(condition.rightOperand[0]);
          }
        }
      }

      for (const source of sourceList) {
        flowSorter(source, new Set(visited), calculationInfo); // Pass a new set to avoid mutation issues
      }
    }

    visited.delete(flow);

    if (!genSortedDependencyStack.includes(flow)) {
      genSortedDependencyStack.push(flow);
    }
  };
  for (const flow in calculationInfo.flows) {
    genDependencyTracker[flow] = false;
  }

  try {
    for (const flow in genDependencyTracker) {
      if (!genSortedDependencyStack.includes(flow)) {
        flowSorter(flow, new Set(), calculationInfo);
      }
    }
  } catch (e: any) {
    return {
      error: true,
      message: e.message,
      orderedFlows: [] as string[],
    };
  }

  return {
    error: false,
    message: '',
    orderedFlows: genSortedDependencyStack,
  };
};
export const isFlowUsed = (
  flowKeys: string[],
  flows: any,
  flowId: string,
  calculationInfo: ICalculationInfo,
): boolean => {
  if (!flowKeys || !flows || !flowId) return false;
  for (let index = 0; index < flowKeys.length; index++) {
    const key = flowKeys[index];
    for (const flow of flows[key]) {
      if (flow.source.includes(flowId)) {
        return true;
      }

      const flowFilter = flow.filter;

      if (flowFilter) {
        // Use some() instead of forEach() to properly return from the main function
        const hasMatchingCondition = calculationInfo.conditionFilters[
          flowFilter
        ][0].conditions.some((condition: any) => {
          if (
            condition.leftOperandType.includes('flow') &&
            condition.leftOperand.includes(flowId)
          ) {
            return true;
          }
          if (
            condition.rightOperandType.includes('flow') &&
            condition.rightOperand.includes(flowId)
          ) {
            return true;
          }
          return false;
        });

        if (hasMatchingCondition) {
          return true;
        }
      }
    }
  }
  return false;
};
/**
 *
 * @description return a name for calculation or parameter based on type
 * @param type 'parameter'|'calculation'
 * @param savedData ICurrentCalculationSession[] | IParameterSessionProps[],
 * @param isUppercase  boolean
 * @returns string
 */
export const getNameFor = (
  type: 'parameter' | 'calculation',
  savedData: ICurrentCalculationSession[] | IParameterSessionProps[],
  isUppercase = false,
) => {
  let totalSaved = savedData.length;
  const nameArray = savedData.map((item) => item.name);
  let baseName =
    type.toLowerCase() === 'parameter'
      ? isUppercase
        ? 'PARAMETER'
        : 'parameter'
      : isUppercase
        ? 'CALCULATION'
        : 'calculation';
  let generatedName = `${baseName}_${totalSaved + 1}`;
  while (nameArray.includes(generatedName)) {
    totalSaved++;
    generatedName = `${baseName}_${totalSaved + 1}`;
  }
  return generatedName;
};
export const formatDateToYYYYMMDD = (date: any): string => {
  let formated = format(new Date(date), 'yyyy-MM-dd');

  return formated;
};
/**
 *
 * @param obj
 * @returns object with keys in lowercase
 */
export function convertKeysToLowercase<T extends Record<string, any>>(obj: T): Record<string, any> {
  return Object.keys(obj).reduce(
    (acc, key) => {
      acc[key.toLowerCase()] = obj[key];
      return acc;
    },
    {} as Record<string, any>,
  );
}
export const isValidNNumber = (value: string, isDecimal = false) => {
  const numberPattern = isDecimal ? /^-?\d+(\.\d+)?$/ : /^-?\d+$/;

  return numberPattern.test(value.trim());
};
export function isValidEvaluation(evaluation: ICondition, leftOperandType: string) {
  if (!evaluation || !leftOperandType) return false;
  if (evaluation.filterType === 'Pick List') return evaluation.operator === 'in';

  if (
    ['null', 'undefined', 'NaN', '', null, undefined].some((value) =>
      evaluation.leftOperandType.includes(value!),
    )
  )
    return false;
  if (evaluation.filterType === 'Search Condition') {
    if (
      equalPatternCollections.some((collection) => collection.key === evaluation.operator) &&
      evaluation.rightOperand!.includes('NaN')
    )
      return false;
    const operator = evaluation.operator;
    if (operator === 'blank') return true;
    if (evaluation.rightOperand?.length === 0) return false;
    const val0 = evaluation.rightOperand?.[0];
    const val1 = evaluation.rightOperand?.[1];
    const val0T = evaluation.rightOperandType?.[0];
    const val1T = evaluation.rightOperandType?.[1];

    const isParameterised0 =
      evaluation.parameterId[0] !== undefined && evaluation.parameterId[0] !== null;
    const isParameterised1 =
      evaluation.parameterId[1] !== undefined && evaluation.parameterId[1] !== null;

    if (!val0T) return false;
    if (val0 === null || val0 === undefined) return false;
    /**
     * flow field types are check in  the time of drop
     */
    if (['field', 'flow'].includes(val0T)) return true;

    // if the parameterised value is text then it is valid
    if (val0T === 'text' && isParameterised0) {
      return true;
    }
    // if the parameterised value is decimal or integer then it is valid
    if (isParameterised0 && ['decimal', 'integer'].includes(val0T)) {
      return isValidNNumber(val0.toString(), val0T === 'decimal');
    }

    if (operator === 'between') {
      if (val1 === null || val1 === undefined || !val1T) return false;
      /**
       * flow field types are check in  the time of drop
       */
      if (['field', 'flow'].includes(val1T)) return true;

      // if the operator is between and the parameterised value
      if (isParameterised1) {
        return (
          isValidNNumber(val0.toString(), val0T === 'decimal') &&
          isValidNNumber(val1!.toString(), val0T === 'decimal')
        );
      }
    }

    if (['decimal', 'integer'].includes(leftOperandType!)) {
      const isDecimal = leftOperandType === 'decimal';
      if (operator === 'between') {
        return (
          isValidNNumber(val0.toString(), isDecimal) && isValidNNumber(val1!.toString(), isDecimal)
        );
      } else return isValidNNumber(val0.toString(), isDecimal);
    } else if (['DATE', 'DATETIME', 'TIMESTAMP'].includes(leftOperandType!.toUpperCase())) {
      if (operator === 'between') {
        return new Date(val0 as string).getTime() < new Date(val1 as string).getTime();
      } else return new Date(val0 as string).getTime() > 0;
    } else {
      const val0NotEmpty = (val0 as string).trim() !== '';
      if (operator === 'between') {
        const val1NotEmpty = (val1 as string).trim() !== '';
        return val0NotEmpty && val1NotEmpty;
      }

      return val0NotEmpty;
    }
  } else {
    // if (!evaluation.relativeCondition) return false;
  }
  return true;
}
export function getLimitsOfSource(func: SourceFunction) {
  return lengthOfSourceArray[func];
}
export function getLeftOperandType(
  calculationInfo: ICalculationInfo,
  leftOperandType: string,
  leftOperand: string,
) {
  if (!calculationInfo || !leftOperandType) return undefined;
  if (leftOperandType === 'field') return calculationInfo.fields[leftOperand].dataType;
  else {
    const flowName = calculationInfo.flows[leftOperand][0].flow;
    const dataType =
      flowName === 'IfElse'
        ? calculationInfo.selectedResultType[leftOperand as string]
        : calculationInfo?.flows[leftOperand][0]?.resultantDatatype;

    return dataType;
  }
}
export function isFlowFilledProperly(calculationInfo: ICalculationInfo, flowId: string) {
  const flow = calculationInfo.flows[flowId];
  if (!flow) return false;
  for (const subFlow of flow) {
    const name = subFlow.flow;
    const sources = subFlow.source;
    const filterId = subFlow.filter;
    const requirement = getLimitsOfSource(name as SourceFunction);

    if (!requirement) return false;

    if (sources.length < requirement.min || sources.length > requirement.max) return false;
    if (name === 'IfElse' && subFlow.condition !== 'else') {
      if (!filterId) return false;
      const filter = calculationInfo.conditionFilters[filterId];
      if (!filter) return false;
      if (filter[0].conditions.length === 0) return false;

      const evaluations = filter[0].conditions;
      if (filter[0].conditions.length === 0) return false;
      for (const condition of evaluations) {
        if (
          !isValidEvaluation(
            condition,
            getLeftOperandType(
              calculationInfo,
              condition.leftOperandType[0],
              condition.leftOperand[0],
            )!,
          )
        )
          return false;
      }
    }
  }

  return true;
}
export function hasKeyInNestedObjects(
  obj: Record<any, any[]>,
  targetKey: string,
  returnValue = false,
) {
  for (const key in obj) {
    const arr = obj[key];
    for (const item of arr) {
      if (item && typeof item === 'object' && targetKey in item) {
        return {
          hasKey: true,
          value: returnValue ? item[targetKey] : undefined,
        };
      }
    }
  }
  return {
    hasKey: false,
    value: undefined,
  };
}

export function getOperatorPattern(type: string) {
  switch (type) {
    case 'integer':
    case 'decimal':
      return equalPatternCollections;
    case 'text':
      return withPatternCollections;
    case 'timestamp':
    case 'date':
      return equalPatternCollections;
  }
  return withPatternCollections;
}

/**
 * Generic helper to extract all Flow IDs referenced within a specific step.
 * It handles looking into direct sources and nested If/Else conditions.
 */
function getFlowDependenciesFromStep(
  step: any,
  calculationInfo: ICalculationInfo,
  checkSources: boolean,
  checkConditions: boolean,
): string[] {
  const dependencies: string[] = [];

  // 1. Check Direct Sources
  if (checkSources && step.source && step.sourceType) {
    step.source.forEach((sourceId: string, index: number) => {
      if (step.sourceType[index] === 'flow') {
        dependencies.push(sourceId);
      }
    });
  }

  // 2. Check Conditional Filters (IfElse)
  if (checkConditions && step.flow === 'IfElse' && step.filter && step.condition !== 'else') {
    const filter = calculationInfo.conditionFilters?.[step.filter];
    if (filter && filter[0]?.conditions) {
      filter[0].conditions.forEach((condition: any) => {
        // Check Left Operand
        if (condition.leftOperandType?.[0] === 'flow') {
          dependencies.push(condition.leftOperand[0]);
        }
        // Check Right Operands (Array)
        if (condition.rightOperandType && condition.rightOperand) {
          condition.rightOperandType.forEach((type: string, index: number) => {
            if (type === 'flow') {
              dependencies.push(condition.rightOperand[index]);
            }
          });
        }
      });
    }
  }

  return dependencies;
}

/**
 * Checks if a flow is already present as a source in a specific step (identified by index) of another flow.
 * Handles all flow types: direct sources, conditional filters (IfElse), and nested flow references.
 *
 * @param calculationInfo The ICalculationInfo object containing all flow data.
 * @param flowToBeDropped The ID of the flow being dragged (e.g., 'f3').
 * @param toBeDroppedAtFlow The ID of the parent flow (e.g., 'f1').
 * @param activeCondition The index of the specific step within the parent flow being dropped onto (e.g., 0).
 * @returns true if the flow is already in the step's source, false otherwise.
 */
export function isFlowAlreadyPresentInStep(
  calculationInfo: ICalculationInfo,
  flowToBeDropped: string,
  toBeDroppedAtFlow: string,
  activeCondition: number,
  dropZoneType: 'source' | 'condition',
): boolean {
  const targetFlowSteps = calculationInfo.flows[toBeDroppedAtFlow];
  if (!targetFlowSteps || targetFlowSteps.length <= activeCondition) return false;

  const targetStep = targetFlowSteps[activeCondition];
  if (!targetStep) return false;

  const checkSources = dropZoneType === 'source';
  const checkConditions = dropZoneType === 'condition';

  // Get all flows currently used in this step based on the criteria
  const usedFlows = getFlowDependenciesFromStep(
    targetStep,
    calculationInfo,
    checkSources,
    checkConditions,
  );

  // Check if our flow is one of them
  return usedFlows.includes(flowToBeDropped);
}

/**
 * Checks if dropping a flow will create a cycle in the calculation.
 * You wanna drop f3 flow into f1 flow
 * @param flowToBeDropped is f3
 * @param toBeDroppedAtFlow is f1
 * @returns true if dropping the flow will create a cycle, false otherwise
 */
export function willCreateCycle(
  calculationInfo: ICalculationInfo,
  flowToBeDropped: string,
  toBeDroppedAtFlow: string,
): boolean {
  const visited = new Set<string>();

  function backTrack(currentFlowId: string): boolean {
    if (visited.has(currentFlowId)) return false;
    visited.add(currentFlowId);

    const flowInfo = calculationInfo.flows[currentFlowId];
    if (!flowInfo) return false;

    for (const step of flowInfo) {
      const dependencies = getFlowDependenciesFromStep(
        step,
        calculationInfo,
        true, // checkSources
        true, // checkConditions
      );

      for (const dependentFlowId of dependencies) {
        // 1. Direct Match
        if (dependentFlowId === toBeDroppedAtFlow) return true;

        // 2. Recursive
        if (backTrack(dependentFlowId)) return true;
      }
    }
    return false;
  }
  return backTrack(flowToBeDropped);
}

export const adjustSourceLength = (
  flowUID: string,
  flowName: string,
  currentCalculationSession: ICurrentCalculationSession | null,
) => {
  if (!flowUID) return 0;
  switch (flowName.toLocaleLowerCase()) {
    case 'log': {
      const sourceLength =
        currentCalculationSession?.calculationInfo.flows[flowUID][0].sourceType.filter((src: any) =>
          ['flow', 'integer', 'decimal', 'field'].includes(src),
        ).length ?? 0;
      if (sourceLength === 1) {
        return 0;
      } else if (sourceLength === 2) {
        return 1;
      }
      break;
    }

    case 'substringleft': {
      const sourceLength =
        currentCalculationSession?.calculationInfo.flows[flowUID][0].sourceType.filter(
          (src: any) =>
            src === 'field' ||
            src === 'decimal' ||
            src === 'integer' ||
            src === 'text' ||
            src === 'flow',
        ).length ?? 0;
      if (sourceLength === 3) {
        return 1;
      } else if (sourceLength < 3) {
        return 0;
      }
      break;
    }

    case 'substringright': {
      const sourceLength =
        currentCalculationSession?.calculationInfo.flows[flowUID][0].sourceType.filter(
          (src: any) =>
            src === 'field' ||
            src === 'decimal' ||
            src === 'integer' ||
            src === 'text' ||
            src === 'flow',
        ).length ?? 0;
      if (sourceLength === 3) {
        return 1;
      } else if (sourceLength < 3) {
        return 0;
      }
      break;
    }

    case 'split': {
      const sourceLength =
        currentCalculationSession?.calculationInfo.flows[flowUID][0].sourceType.filter(
          (src: any) =>
            src === 'field' ||
            src === 'decimal' ||
            src === 'integer' ||
            src === 'text' ||
            src === 'flow',
        ).length ?? 0;
      if (sourceLength === 4) {
        return 1;
      } else if (sourceLength < 4) {
        return 0;
      }
      break;
    }

    case 'concat': {
      const sourceLength =
        currentCalculationSession?.calculationInfo.flows[flowUID][0].sourceType.filter(
          (src: any) => src === 'field' || src === 'text' || src === 'flow',
        ).length ?? 0;
      return sourceLength;
    }

    default:
      return (
        currentCalculationSession?.calculationInfo?.flows[flowUID][0].sourceType.filter(
          (src: any) => src === 'field' || src === 'decimal' || src === 'integer' || src === 'flow',
        ).length ?? 0
      );
  }
};

export const isFlowUsedInOtherFlowsAsSource = (
  flowId: string,
  currentCalculationSession: ICurrentCalculationSession | null,
) => {
  const allFlowsWithFlowAsSource = Object.keys(
    currentCalculationSession?.calculationInfo?.flows ?? {},
  ).filter((flow: any) =>
    currentCalculationSession?.calculationInfo.flows[flow][0].sourceType.includes('flow'),
  );

  for (const flow of allFlowsWithFlowAsSource) {
    if (currentCalculationSession?.calculationInfo.flows[flow][0].source.includes(flowId))
      return true;
  }

  return Object.values(currentCalculationSession?.calculationInfo?.conditionFilters ?? {}).some(
    (filter: any) =>
      filter[0].conditions.some(
        (condition: any) =>
          condition.leftOperand[0] === flowId || condition.rightOperand[0] === flowId,
      ),
  );
};

type Viewport = {
  x: number;
  y: number;
  zoom: number;
};

export const isNodeOutOfViewport = (
  node: Node,
  viewport: Viewport,
  containerWidth: number,
  containerHeight: number,
): boolean => {
  if (!node) return false;

  const { x, y, zoom } = viewport;

  const width = node.measured?.width ?? 0;
  const height = node.measured?.height ?? 0;
  if (!width || !height) return false;
  const nodeLeft = node.position.x;
  const nodeTop = node.position.y;
  const nodeRight = nodeLeft + width;
  const nodeBottom = nodeTop + height;

  const viewLeft = -x / zoom;
  const viewTop = -y / zoom;
  const viewRight = viewLeft + containerWidth / zoom;
  const viewBottom = viewTop + containerHeight / zoom;
  // console.log({ viewLeft, viewTop, viewRight, viewBottom })
  return (
    nodeLeft < viewLeft || nodeTop < viewTop || nodeRight > viewRight || nodeBottom > viewBottom
  );
};
